/**
 * Multi-select column header filters — legacy Tabulator's `headerFilter: "list"`. A dropdown whose
 * options are the distinct values **present in the loaded result**, narrowing the rows on screen.
 *
 * This is the one local control §4.4 allows, and it inherits the whole condition:
 *
 * - **Offer it only over a complete result.** Over a truncated one the option list is a short list
 *   of lines presented as the set — the §4.3 failure applied to a dimension, and unlike a wrong
 *   total it prompts no question. {@link headerFiltersAreOffered} is the check.
 * - **It selects rows; it classifies none.** `String(...)` equality on server-returned values —
 *   no threshold, no bucketing, no derived membership (§5.2).
 * - **It does not narrow the export** (§5.9); a report offering it says so while a filter is active.
 */

import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

/** Selected values per field. An absent key or an empty array means "all" — no filter. */
export type HeaderFilterSelection = Readonly<Record<string, readonly string[]>>;

/** Past this many options the list gets its own search box. */
const SEARCHABLE_ABOVE = 8;

/**
 * The completeness gate (§4.3/§4.4). Both halves matter: `truncated` is the server saying it
 * stopped early, and the row/total comparison catches any other way the two could disagree.
 */
export function headerFiltersAreOffered(
  response: { rows: readonly unknown[]; total: number; truncated: boolean } | undefined,
): boolean {
  return response !== undefined && !response.truncated && response.rows.length === response.total;
}

/** The distinct values of one field. `numeric: true` so "Line 10" sorts after "Line 2". */
export function distinctValues(
  rows: readonly Record<string, unknown>[],
  field: string,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const raw = row[field];
    if (raw === null || raw === undefined || raw === '') continue;
    seen.add(String(raw));
  }
  return [...seen].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/**
 * The distinct values of a `GROUP_CONCAT` cell, split into parts — otherwise the list offers
 * "adidas,TOP ROYAL" as one option and a reader can never select "adidas".
 */
export function distinctListValues(
  rows: readonly Record<string, unknown>[],
  field: string,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const raw = row[field];
    if (raw === null || raw === undefined || raw === '') continue;
    for (const part of String(raw).split(',')) {
      const value = part.trim();
      if (value !== '') seen.add(value);
    }
  }
  return [...seen].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/** A list cell matches when any one of its values was selected. */
export function listMatch(value: unknown, selected: readonly string[] | undefined): boolean {
  if (selected === undefined || selected.length === 0) return true;
  if (value === null || value === undefined || value === '') return false;
  const chosen = new Set(selected);
  return String(value)
    .split(',')
    .some((part) => chosen.has(part.trim()));
}

/** One field's verdict on one row. Nothing selected accepts everything. */
export function multiMatch(value: unknown, selected: readonly string[] | undefined): boolean {
  if (selected === undefined || selected.length === 0) return true;
  return selected.includes(value === null || value === undefined ? '' : String(value));
}

/**
 * Whether a row survives every field's selection. `listFields` names the comma-joined ones, which
 * match if any one of their values was selected; the rest match on the whole cell.
 */
export function matchesHeaderFilters(
  row: Record<string, unknown>,
  fields: readonly string[],
  selection: HeaderFilterSelection,
  listFields?: ReadonlySet<string>,
): boolean {
  return fields.every((field) =>
    listFields?.has(field) === true
      ? listMatch(row[field], selection[field])
      : multiMatch(row[field], selection[field]),
  );
}

/** Build the option list for every filtered field in one pass over the rows. */
export function headerFilterOptions(
  rows: readonly Record<string, unknown>[],
  fields: readonly string[],
  listFields?: ReadonlySet<string>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const field of fields) {
    map[field] =
      listFields?.has(field) === true
        ? distinctListValues(rows, field)
        : distinctValues(rows, field);
  }
  return map;
}

export interface HeaderFilterProps {
  label: string;
  field: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (field: string, next: string[]) => void;
  /**
   * How an option is **shown**; the value itself is untouched. For date columns, whose cells render
   * "3 Jun 2026" over a raw `2026-06-03T00:00:00`. Sorting stays on the raw value, which for an ISO
   * date is chronological — formatting first would put 1 June after 10 June.
   */
  formatOption?: (value: string) => string;
}

/** The column title with its dropdown beneath. */
export function HeaderFilter({
  label,
  field,
  options,
  selected,
  onChange,
  formatOption,
}: HeaderFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedSet = new Set(selected);
  const show = (value: string) => formatOption?.(value) ?? value;

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return options;
    // Match the shown text as well as the raw value: "Jun" must find "3 Jun 2026".
    return options.filter((option) => {
      const label = formatOption?.(option) ?? option;
      return option.toLowerCase().includes(q) || label.toLowerCase().includes(q);
    });
  }, [options, query, formatOption]);

  const toggle = (value: string) => {
    const next = selectedSet.has(value)
      ? selected.filter((each) => each !== value)
      : [...selected, value];
    // Emit in option order, so the URL does not record the sequence the reader happened to click.
    onChange(
      field,
      options.filter((option) => next.includes(option)),
    );
  };

  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1 && selected[0] !== undefined
        ? show(selected[0])
        : `${String(selected.length)} selected`;

  return (
    <div className="flex min-w-20 max-w-36 flex-col gap-0.5 py-0.5">
      <span className="whitespace-normal break-words [line-height:1.2]">{label}</span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Filter ${label}`}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              'h-6 w-full min-w-0 justify-between gap-0.5 border-table-header-border/40 bg-background/95 px-1',
              'text-[11px] font-normal text-foreground hover:bg-background',
              selected.length === 0 && 'text-muted-foreground',
            )}
            // The header is also the sort control; a click on the filter is not a click on it.
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <span className="truncate">{summary}</span>
            <FontAwesomeIcon icon={faChevronDown} className="size-2.5 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-56 p-0"
          align="start"
          onOpenAutoFocus={(event) => {
            // Keep focus available for the search box without jumping the table.
            event.preventDefault();
          }}
        >
          <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">
              {selected.length === 0 ? 'All' : `${String(selected.length)} selected`}
            </span>
            {selected.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-[11px]"
                onClick={() => {
                  onChange(field, []);
                }}
              >
                Clear
              </Button>
            ) : null}
          </div>
          {options.length > SEARCHABLE_ABOVE ? (
            <div className="border-b p-1.5">
              <Input
                value={query}
                placeholder="Filter list…"
                className="h-7 text-xs"
                autoFocus
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
            </div>
          ) : null}
          <ScrollArea className="h-48">
            {filteredOptions.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">No values</p>
            ) : (
              <ul role="listbox" aria-multiselectable aria-label={label} className="p-1">
                {filteredOptions.map((option) => {
                  const checked = selectedSet.has(option);
                  return (
                    <li key={option}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={checked}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs',
                          'hover:bg-accent hover:text-accent-foreground',
                          checked && 'bg-accent/50',
                        )}
                        onClick={() => {
                          toggle(option);
                        }}
                      >
                        <span
                          className={cn(
                            'flex size-3.5 shrink-0 items-center justify-center rounded-sm border',
                            checked
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input',
                          )}
                          aria-hidden
                        >
                          {checked ? (
                            <FontAwesomeIcon icon={faCheck} className="size-2" />
                          ) : null}
                        </span>
                        <span className="truncate">{show(option)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Replace the header of every filtered column with {@link HeaderFilter}. Top-level leaves only —
 * a filtered field is an identity column, and those sit under no group heading in these reports.
 */
export function withHeaderFilters<TRow>(
  columns: ColumnDef<TRow>[],
  fields: readonly string[],
  selection: HeaderFilterSelection,
  optionsByField: Record<string, readonly string[]>,
  onChange: (field: string, next: string[]) => void,
  /** Per-field option labelling — see {@link HeaderFilterProps.formatOption}. */
  formatOption?: (field: string, value: string) => string,
): ColumnDef<TRow>[] {
  const filtered = new Set(fields);
  return columns.map((column): ColumnDef<TRow> => {
    const id = column.id;
    if (id === undefined || !filtered.has(id)) {
      return column;
    }
    const label = typeof column.header === 'string' ? column.header : id;
    return {
      ...column,
      id,
      header: () => (
        <HeaderFilter
          label={label}
          field={id}
          options={optionsByField[id] ?? []}
          selected={selection[id] ?? []}
          onChange={onChange}
          formatOption={
            formatOption === undefined ? undefined : (value) => formatOption(id, value)
          }
        />
      ),
    } as ColumnDef<TRow>;
  });
}
